import { Directive, ElementRef, NgZone, OnDestroy, OnInit, computed, effect, inject, input, signal } from '@angular/core';

import { DsTooltipTrigger } from './tooltip.directives';

// Description: Directive to control tooltip visibility based on mouse events
@Directive({
    selector: '[dsTooltipOnFocus]',
    standalone: true,
})
export class DsTooltipOnFocus implements OnInit, OnDestroy {
    private readonly tooltipTrigger = inject(DsTooltipTrigger);
    private readonly ngZone = inject(NgZone);
    private readonly elementRef = inject(ElementRef);

    public readonly closeDelay = input(200);

    private readonly hideTimeoutId = signal<ReturnType<typeof setTimeout> | undefined>(undefined);
    private readonly isMouseInside = signal(false);
    private readonly isKeyboardFocused = computed(() => this.tooltipTrigger.isKeyboardFocused() && !this.tooltipTrigger.interactive());

    constructor() {
        effect(() => {
            if (this.isMouseInside() || this.isKeyboardFocused()) {
                this.showTooltip();
            } else {
                this.hideTooltip();
            }
        });
    }

    ngOnInit(): void {
        this.ngZone.runOutsideAngular(() => {
            this.addEventListeners();
        });
    }

    private addEventListeners(): void {
        const element = this.elementRef.nativeElement as HTMLElement;
        element.addEventListener('mouseenter', this.onMouseEnter);
        element.addEventListener('mouseleave', this.onMouseLeave);

        const tooltipElement = this.tooltipTrigger.overlayRef?.overlayElement;
        tooltipElement?.addEventListener('mouseenter', this.onTooltipMouseEnter);
        tooltipElement?.addEventListener('mouseleave', this.onTooltipMouseLeave);
    }

    private removeEventListeners(): void {
        const element = this.elementRef.nativeElement as HTMLElement;
        element.removeEventListener('mouseenter', this.onMouseEnter);
        element.removeEventListener('mouseleave', this.onMouseLeave);

        const tooltipElement = this.tooltipTrigger.overlayRef?.overlayElement;
        tooltipElement?.removeEventListener('mouseleave', this.onTooltipMouseLeave);
    }

    private showTooltip(): void {
        if (!this.tooltipTrigger.overlayRef?.hasAttached()) {
            this.tooltipTrigger.toggleTooltip();

            // Ensure position is recalculated after attaching
            setTimeout(() => {
                this.tooltipTrigger.overlayRef?.updatePosition();
            }, 0);

            this.tooltipTrigger.overlayRef?.detachments().subscribe(() => {
                this.resetTooltipState();
            });
        }
    }

    private hideTooltip(): void {
        if (this.tooltipTrigger.overlayRef?.hasAttached()) {
            this.tooltipTrigger.toggleTooltip();
        }
    }

    private readonly onTooltipMouseEnter = (): void => {
        this.isMouseInside.set(true);

        // Cancel any scheduled hide when entering the tooltip
        if (this.hideTimeoutId()) {
            clearTimeout(this.hideTimeoutId());
            this.hideTimeoutId.set(undefined);
        }
    };

    private readonly onMouseEnter = (): void => {
        this.isMouseInside.set(true);

        // Clear the hide timeout if the mouse re-enters the tooltip
        this.ngZone.runOutsideAngular(() => {
            if (this.hideTimeoutId()) {
                clearTimeout(this.hideTimeoutId());
                this.hideTimeoutId.set(undefined);
            }
        });
    };

    // Mouse leave event handler (adds a delay before hiding)
    private readonly onMouseLeave = (event: MouseEvent): void => {
        if (!this.isLeavingTooltip(event)) {
            this.scheduleHide();
        }
    };

    private readonly onTooltipMouseLeave = (event: MouseEvent): void => {
        if (!this.isLeavingTooltip(event)) {
            this.scheduleHide();
        }
    };

    private isLeavingTooltip(event: MouseEvent): boolean {
        const tooltipElement = this.tooltipTrigger.overlayRef?.overlayElement;
        return (
            !!tooltipElement &&
            (tooltipElement.contains(event.relatedTarget as Node) || (event.currentTarget as HTMLElement).contains(event.relatedTarget as Node))
        );
    }

    private scheduleHide(): void {
        this.ngZone.runOutsideAngular(() => {
            this.hideTimeoutId.set(
                setTimeout(() => {
                    this.isMouseInside.set(false);
                    this.resetTooltipState();
                }, this.closeDelay()),
            );
        });
    }

    private resetTooltipState(): void {
        this.isMouseInside.set(false);
        this.hideTooltip();
    }

    ngOnDestroy(): void {
        this.ngZone.runOutsideAngular(() => {
            if (this.hideTimeoutId()) {
                clearTimeout(this.hideTimeoutId());
            }
        });
        this.removeEventListeners();
    }
}
